import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mic, MicOff, Users, Link2, Coins, Send, Circle, Square, LogOut, Signal, Lock, Globe, PhoneCall, Flag } from "lucide-react";
import { api } from "@/lib/api";
import { outboxAdd } from "@/lib/walkieRtc";
import { useWalkie, Avatar, notify } from "./WalkieLayout";
import TipDialog from "./TipDialog";

const QCOLOR = { good: "#39FF14", fair: "#FFE300", poor: "#FF003C" };

function PttButton({ disabled, talking, busyName, onDown, onUp }) {
  return (
    <div className="flex flex-col items-center py-4 select-none">
      <button disabled={disabled} data-testid="walkie-ptt-btn" data-talking={talking ? "1" : "0"}
        onPointerDown={(e) => { e.preventDefault(); e.currentTarget.setPointerCapture?.(e.pointerId); onDown(); }}
        onPointerUp={onUp} onPointerCancel={onUp} onContextMenu={(e) => e.preventDefault()}
        className={`relative w-56 h-56 rounded-full flex flex-col items-center justify-center font-black text-black transition-transform touch-none
          ${talking ? "scale-95 bg-gradient-to-br from-[#39FF14] to-[#00F0FF] shadow-[0_0_90px_rgba(57,255,20,.6)]" : "bg-gradient-to-br from-[#FF6A00] via-[#FF2BD6] to-[#7B2BFF] shadow-[0_0_60px_rgba(255,43,214,.45)]"}
          disabled:opacity-40 disabled:grayscale`} style={{ WebkitTapHighlightColor: "transparent" }}>
        {talking && <span className="absolute inset-0 rounded-full animate-ping bg-[#39FF14]/30" />}
        {talking ? <Mic className="w-14 h-14" /> : <MicOff className="w-14 h-14" />}
        <span className="mt-2 text-sm uppercase tracking-widest">{talking ? "Talking…" : "Hold to talk"}</span>
      </button>
      <div className="h-5 mt-3 text-xs wt-muted" data-testid="walkie-floor-status">{busyName ? `${busyName} is talking` : talking ? "Release to stop" : "Channel is clear"}</div>
    </div>
  );
}

function useRecorder(channelId, status, onSent) {
  const [rec, setRec] = useState(null);
  const chunks = useRef([]); const started = useRef(0);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunks.current = []; started.current = Date.now();
      mr.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks.current, { type: mr.mimeType || "audio/webm" });
        const duration = (Date.now() - started.current) / 1000;
        const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        if (status !== "online" || !navigator.onLine) {
          await outboxAdd({ id, channel_id: channelId, blob, duration, created: Date.now() });
          toast("Saved offline — it will send when you reconnect", { description: "Turn on auto-send in Profile, or tap Send queued." });
          onSent(true); return;
        }
        const fd = new FormData();
        fd.append("file", blob, "voice.webm"); fd.append("duration", String(duration)); fd.append("client_msg_id", id);
        try { await api.post(`/walkie/channels/${channelId}/voice`, fd); onSent(); }
        catch (e) {
          if (!e?.response) { await outboxAdd({ id, channel_id: channelId, blob, duration, created: Date.now() }); onSent(true); }
          else notify.err(e);
        }
      };
      mr.start(); setRec(mr);
      setTimeout(() => mr.state === "recording" && mr.stop(), 120000);
    } catch { toast.error("Microphone permission is needed to record"); }
  };
  const stop = () => { rec?.state === "recording" && rec.stop(); setRec(null); };
  return { recording: !!rec, start, stop };
}

function Messages({ channelId, messages, status, onSent }) {
  const [text, setText] = useState("");
  const { recording, start, stop } = useRecorder(channelId, status, onSent);
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ block: "nearest" }); }, [messages.length]);
  const sendText = async (e) => {
    e.preventDefault(); if (!text.trim()) return;
    try { await api.post(`/walkie/channels/${channelId}/text`, { text, client_msg_id: `${Date.now()}` }); setText(""); onSent(); } catch (er) { notify.err(er); }
  };
  return (
    <section className="wt-card rounded-2xl p-3" data-testid="walkie-messages">
      <div className="text-xs font-black uppercase tracking-widest wt-muted mb-2">Voice & text messages</div>
      <div className="max-h-72 overflow-y-auto space-y-2 pr-1">
        {messages.length === 0 && <div className="text-xs wt-muted">No messages yet.</div>}
        {messages.map((m) => (
          <div key={m.id} className="flex gap-2 items-start" data-testid={`walkie-msg-${m.id}`}>
            <Avatar p={{ display_name: m.from_name, avatar_url: m.from_avatar }} size={28} />
            <div className="min-w-0 flex-1">
              <div className="text-[11px] wt-muted">{m.from_name} · {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}{m.recorded_offline ? " · sent after reconnect" : ""}</div>
              {m.kind === "voice" ? <audio controls src={m.audio_url} className="h-8 w-full max-w-xs mt-0.5" preload="none" /> : <div className="text-sm break-words">{m.text}</div>}
            </div>
            <button onClick={() => api.post("/walkie/report", { target_type: "message", target_id: m.id }).then(() => toast.success("Reported"))} className="wt-muted hover:text-[#FF003C]" aria-label="Report message"><Flag className="w-3 h-3" /></button>
          </div>
        ))}
        <div ref={end} />
      </div>
      <form onSubmit={sendText} className="flex gap-2 mt-3">
        <button type="button" onClick={recording ? stop : start} data-testid="walkie-record-btn"
          className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${recording ? "bg-[#FF003C] text-white animate-pulse" : "wt-card"}`} aria-label="Record voice message">
          {recording ? <Square className="w-4 h-4" /> : <Circle className="w-4 h-4 text-[#FF003C]" fill="#FF003C" />}
        </button>
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={500} placeholder="Type a message…" data-testid="walkie-text-input" className="wt-input flex-1 rounded-full px-4 text-sm outline-none" />
        <button data-testid="walkie-text-send" className="w-10 h-10 rounded-full bg-[#00F0FF] text-black flex items-center justify-center shrink-0"><Send className="w-4 h-4" /></button>
      </form>
    </section>
  );
}

export default function WalkieRoom() {
  const { id } = useParams();
  const nav = useNavigate();
  const { me, engine, subscribe, status } = useWalkie();
  const [ch, setCh] = useState(null);
  const [joined, setJoined] = useState(false);
  const [live, setLive] = useState({});
  const [speaker, setSpeaker] = useState(null);
  const [talking, setTalking] = useState(false);
  const [myQ, setMyQ] = useState(null);
  const [messages, setMessages] = useState([]);
  const [tipTo, setTipTo] = useState(null);
  const [listenOnly, setListenOnly] = useState(false);
  const wake = useRef(null);
  const myId = me.profile.user_id;

  const load = useCallback(() => api.get(`/walkie/channels/${id}`).then((r) => setCh(r.data)).catch((e) => { notify.err(e); nav("/walkie/channels"); }), [id, nav]);
  const loadMsgs = useCallback(() => api.get(`/walkie/channels/${id}/messages`).then((r) => setMessages(r.data.messages)).catch(() => {}), [id]);
  useEffect(() => { load(); loadMsgs(); }, [load, loadMsgs]);

  useEffect(() => subscribe((type, a, b) => {
    if (type === "peers") setLive(Object.fromEntries(a.map((p) => [p.uid, p])));
    if (type === "speaking") { setSpeaker(a); if (a !== myId) setTalking(false); if (b === "timeout" && a === null) toast("Talk time limit reached"); }
    if (type === "busy") { setTalking(false); toast("Someone else is talking — wait for a clear channel"); }
    if (type === "peer_join") load();
    if (type === "message") setMessages((m) => (m.some((x) => x.id === a.message.id) ? m : [...m, a.message]));
    if (type === "tip") toast.success(`${a.from} tipped ${a.to} ${a.coins} coins`);
    if (type === "closed") { toast.error("This channel was closed"); nav("/walkie/channels"); }
    if (type === "my_quality") setMyQ(a);
    if (type === "autoplay_blocked") toast("Tap anywhere to enable channel audio");
    if (type === "error" && a?.error === "no_access") toast.error("You don't have access to this channel");
  }), [subscribe, myId, load, nav]);

  useEffect(() => () => { engine?.leave(); wake.current?.release?.().catch(() => {}); }, [engine]);

  const join = async () => {
    if (!engine) return;
    if (ch.kind === "public" && !ch.is_member) await api.post(`/walkie/channels/${id}/join`).catch(() => {});
    const ok = await engine.enableMic(me.profile.voice_effect);
    setListenOnly(!ok);
    if (!ok) toast("Microphone blocked — you can listen, but not talk. Allow mic access in your browser settings.");
    engine.join(id); setJoined(true);
    try { wake.current = await navigator.wakeLock?.request("screen"); } catch {}
    load();
  };
  const down = () => { if (engine?.pttStart()) setTalking(true); if (navigator.vibrate) navigator.vibrate(30); };
  const up = () => { if (talking) { engine?.pttStop(); setTalking(false); } };

  useEffect(() => {
    if (!joined || listenOnly) return;
    const kd = (e) => { if (e.code === "Space" && !e.repeat && e.target.tagName !== "INPUT") { e.preventDefault(); down(); } };
    const ku = (e) => { if (e.code === "Space") up(); };
    window.addEventListener("keydown", kd); window.addEventListener("keyup", ku);
    return () => { window.removeEventListener("keydown", kd); window.removeEventListener("keyup", ku); };
  });

  if (!ch) return <div className="py-16 text-center wt-muted animate-pulse">Tuning in…</div>;
  const members = ch.members || [];
  const speakerName = speaker && speaker !== myId ? (members.find((m) => m.user_id === speaker)?.display_name || "Someone") : null;
  const invite = ch.invite_code ? `${window.location.origin}/walkie/join/${ch.invite_code}` : null;
  const leaveChannel = async () => { engine?.leave(); setJoined(false); await api.post(`/walkie/channels/${id}/leave`).then(() => nav("/walkie/channels")).catch(notify.err); };

  return (
    <div className="space-y-4" data-testid="walkie-room">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest wt-muted flex items-center gap-1">
            {ch.kind === "public" ? <Globe className="w-3 h-3" /> : <Lock className="w-3 h-3" />} {ch.kind === "dm" ? "Private conversation" : `${ch.kind} channel`} · {ch.member_count} member{ch.member_count === 1 ? "" : "s"}
          </div>
          <h1 className="font-black text-2xl truncate" data-testid="walkie-room-name">{ch.kind === "dm" ? ch.name : `#${ch.name}`}</h1>
          {ch.description && <p className="text-sm wt-muted">{ch.description}</p>}
        </div>
        {myQ && <span className="text-[10px] font-bold uppercase flex items-center gap-1" style={{ color: QCOLOR[myQ] }} data-testid="walkie-my-quality"><Signal className="w-3.5 h-3.5" />{myQ}</span>}
      </div>

      {!joined ? (
        <button onClick={join} disabled={status !== "online"} data-testid="walkie-join-voice-btn"
          className="w-full py-4 rounded-2xl font-black text-black bg-gradient-to-r from-[#FF6A00] via-[#FF2BD6] to-[#00F0FF] disabled:opacity-40">
          {status === "online" ? "Join voice channel" : status === "connecting" || status === "reconnecting" ? "Connecting…" : "Live voice needs an internet connection"}
        </button>
      ) : (
        <PttButton disabled={listenOnly || status !== "online" || !!speakerName} talking={talking} busyName={speakerName} onDown={down} onUp={up} />
      )}

      <section className="wt-card rounded-2xl p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-black uppercase tracking-widest wt-muted flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Members · {ch.live_count} live</div>
          <div className="flex gap-2">
            {invite && ch.kind !== "dm" && <button onClick={() => { navigator.clipboard.writeText(invite); toast.success("Invite link copied"); }} data-testid="walkie-copy-invite" className="text-[11px] font-bold flex items-center gap-1 text-[#00F0FF]"><Link2 className="w-3.5 h-3.5" /> Invite</button>}
            {ch.is_member && !ch.is_owner && <button onClick={leaveChannel} data-testid="walkie-leave-channel" className="text-[11px] font-bold flex items-center gap-1 text-[#FF003C]"><LogOut className="w-3.5 h-3.5" /> Leave</button>}
          </div>
        </div>
        <div className="space-y-1.5">
          {members.map((m) => {
            const isLive = ch.live?.includes(m.user_id) || live[m.user_id] || m.user_id === myId && joined;
            const q = live[m.user_id]?.quality;
            return (
              <div key={m.user_id} className={`flex items-center gap-3 rounded-xl px-2 py-1.5 ${speaker === m.user_id ? "bg-[#39FF14]/15" : ""}`} data-testid={`walkie-member-${m.user_id}`}>
                <Avatar p={m} size={34} />
                <Link to={m.user_id === myId ? "/walkie/me" : `/walkie/u/${m.user_id}`} className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{m.display_name}{m.user_id === myId && " (you)"}</div>
                  <div className="text-[10px] wt-muted">{speaker === m.user_id ? <span className="text-[#39FF14] font-bold" data-testid="walkie-speaking-indicator">● speaking</span> : isLive ? "in channel" : m.online ? "online" : "offline"}{q && ` · ${q}`}</div>
                </Link>
                {m.user_id !== myId && (
                  <>
                    {!isLive && joined && <button onClick={() => api.post(`/walkie/ring/${m.user_id}`, null, { params: { channel_id: id } }).then(() => toast.success("Call sent")).catch(notify.err)} aria-label="Call member" className="text-[#00F0FF]"><PhoneCall className="w-4 h-4" /></button>}
                    <button onClick={() => setTipTo(m)} data-testid={`walkie-tip-btn-${m.user_id}`} aria-label="Tip member" className="text-[#FFE300]"><Coins className="w-4 h-4" /></button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <Messages channelId={id} messages={messages} status={status} onSent={loadMsgs} />
      <p className="text-[10px] wt-muted text-center">Live voice works over the internet with no distance limit. Background and screen-locked audio depend on your device and browser.</p>
      {tipTo && <TipDialog to={tipTo} channelId={id} onClose={() => setTipTo(null)} />}
    </div>
  );
}
